import {
  Backdrop,
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  getMerchantUserDetails,
  getMerchantUserWithActions,
} from "../../../services/services";
import Label from "../../../components/Label";
import { parseDate, parseDateTime } from "../../../utils/tableFunctions";
import { Clear } from "@mui/icons-material";
import { linkTextStyle, styles } from "../../../utils/styles";
import { handleOpenLink } from "../../../utils/appUtils";
import HeaderHelpPage from "../../../components/HeaderHelpPage";
import PendingDetailsComparison from "../../../components/PendingDetailsComparison";

const MERCHANT_USER_FIELDS = [
  {
    key: "merchant_user_business_entity",
    label: "Business Entities",
    render: (value) =>
      Array.isArray(value) && value.length
        ? value.map((item) => item.business_name).join(", ")
        : "N/A",
  },
  {
    key: "department",
    label: "Department",
    render: (value) => value?.department || "N/A",
  },
  { key: "merchant_group", label: "Merchant Group" },
  { key: "role", label: "Role" },
  { key: "contact_number", label: "Contact Number" },
  { key: "first_name", label: "First Name" },
  { key: "last_name", label: "Last Name" },
  { key: "email", label: "Email" },
];

const AccountDetails = ({ detailsData, itemId, isViewHistory }) => {
  const [merchantUserDetails, setMerchantUserDetails] = useState();
  const [loaded, setLoaded] = useState();
  const { id = itemId } = useParams();
  const useQuery = () => {
    return new URLSearchParams(useLocation().search);
  };
  const action = useQuery().get("action") || (detailsData && "true") || (isViewHistory && "true");
  // Every maker-and-checker view: the pending dialog and the history dialog.
  const isCheckerView = Boolean(action);
  const isHistoryView = Boolean(isViewHistory) || (!detailsData && Boolean(action));
  const mobile = useMediaQuery((theme) => theme.breakpoints.down("lg"));

  useEffect(() => {
    if (!detailsData && !action) {
      getMerchantUserDetails(id, setMerchantUserDetails, setLoaded);
      return;
    }
    if (!detailsData && action) {
      getMerchantUserWithActions(id, setMerchantUserDetails, setLoaded);
      return;
    }

    setMerchantUserDetails(detailsData);
    setLoaded(true);
  }, [action, detailsData, id]);

  const handleBack = () => window.close();

  const InfoColumn = () => {
    return (
      <Grid item xs={6} md={6}>
        <Box
          sx={{
            height: "100%",
            p: 3,
            border: "1px solid #999",
            borderRadius: 2,
          }}
        >
          <Grid container spacing={2} alignItems="center">
            {/* <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">Business Entity</Typography>
            </Grid>
            <Grid
              item
              xs={6}
              md={7}
            >
              {merchantUserDetails?.business_entity?.business_name}
            </Grid> */}

            <Grid item xs={6} md={5} sx={{ alignSelf: "flex-start"}}>
              <Typography variant="subtitle1">Business Entities</Typography>
            </Grid>
            <Grid
              item
              xs={6}
              md={7}
            >
              {merchantUserDetails?.merchant_user_business_entity?.map(
                (item) => (
                  <li key={item.id}>
                    <Typography
                      component="span"
                      sx={{ ...linkTextStyle }}
                      onClick={() =>
                        handleOpenLink(`/3/app/entity-details/${item.id}`)
                      }
                    >
                      {item.business_name}
                    </Typography>
                  </li>
                )
              ) || "N/A"}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">Department</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {merchantUserDetails.department
                ? merchantUserDetails.department.department
                : "N/A"}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">Merchant Group</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {merchantUserDetails?.merchant_group?.name}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">Role</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {merchantUserDetails?.role}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">Contact Number</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {merchantUserDetails.contact_number}
            </Grid>
            {/* <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">Salutation</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {merchantUserDetails.salutation
                ? merchantUserDetails.salutation.salutation
                : "N/A"}
            </Grid> */}
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">First Name</Typography>
            </Grid>
            <Grid
              item
              xs={6}
              md={7}
              sx={{
                overflowWrap: "normal",
                wordBreak: "break-word",
              }}
            >
              {merchantUserDetails.first_name}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">Last Name</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {merchantUserDetails.last_name}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">Email</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {merchantUserDetails.email}
            </Grid>
          </Grid>
        </Box>
      </Grid>
    );
  };

  const DateColumn = () => {
    const requestedOnLabel = isHistoryView ? "Requested on" : "Created on";
    const requestedByLabel = isHistoryView ? "Requested by" : "Created by";
    const reviewedOnLabel = isHistoryView ? "Reviewed on" : "Approved on";
    const reviewedByLabel = isHistoryView ? "Reviewed by" : "Approved by";
    const formatTimestamp = isHistoryView ? parseDateTime : parseDate;

    return (
      <Grid item xs={6} md={6}>
        <Box
          sx={{
            height: "100%",
            p: 3,
            border: "1px solid #999",
            borderRadius: 2,
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">
                {action === "true" ? "Approved" : "Active Now"}
              </Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {action === "true" ? (
                <Label
                  variant="ghost"
                  color={merchantUserDetails.is_approved ? "success" : "error"}
                >
                  {merchantUserDetails.is_approved ? "Yes" : "No"}
                </Label>
              ) : (
                <Label
                  variant="ghost"
                  color={merchantUserDetails.is_active ? "success" : "error"}
                >
                  {merchantUserDetails.is_active ? "YES" : "NO"}
                </Label>
              )}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">{requestedOnLabel}</Typography>
            </Grid>
            <Grid item xs={4} md={7}>
              {merchantUserDetails.created_at ? formatTimestamp(merchantUserDetails.created_at) : "N/A"}
            </Grid>

            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">{requestedByLabel}</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {merchantUserDetails.created_by
                ? merchantUserDetails.created_by.email
                : "N/A"}
            </Grid>

            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">{reviewedOnLabel}</Typography>
            </Grid>
            <Grid item xs={4} md={7}>
              {merchantUserDetails.approved_at ? formatTimestamp(merchantUserDetails.approved_at) : "N/A"}
            </Grid>

            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">{reviewedByLabel}</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {merchantUserDetails.approved_by
                ? merchantUserDetails.approved_by.email
                : "N/A"}
            </Grid>

            {!action && (
              <>
                <Grid item xs={6} md={5}>
                  <Typography variant="subtitle1">Updated by</Typography>
                </Grid>
                <Grid item xs={6} md={7}>
                  {merchantUserDetails.updated_by
                    ? merchantUserDetails.updated_by.email
                    : "N/A"}
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      </Grid>
    );
  };

  return (
    <Container>
      {!detailsData && !itemId && (
        <Stack
          direction="row"
          sx={{
            ...styles.flexBetween,
            mb: 2,
          }}
        >
          <HeaderHelpPage title="Merchant User Details" variant={mobile ? "h5" : "h3"} />

          <Button
            variant="outlined"
            onClick={handleBack}
            endIcon={<Clear />}
          >
            Back
          </Button>
        </Stack>
      )}
      {loaded && action === "true" && (
        <Typography variant="h6" sx={{ mb: 2 }}>
          Action: {merchantUserDetails.action}
        </Typography>
      )}
      {loaded ? (
        <Card sx={{ p: 3 }}>
          <Grid
            container
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 2, sm: 2, md: 2 }}
            sx={{ mt: 5 }}
          >
            {isCheckerView && !merchantUserDetails.original_unavailable ? (
              <>
                <PendingDetailsComparison
                  data={merchantUserDetails}
                  original={merchantUserDetails.original}
                  fields={MERCHANT_USER_FIELDS}
                />
                <DateColumn />
              </>
            ) : (
              <>
                {action && <DateColumn />}
                <InfoColumn />
                {!action && <DateColumn />}
              </>
            )}
          </Grid>
          {merchantUserDetails.reject_message && (
            <>
              <Typography variant="subtitle1" sx={{ mt: 2, display: "flex" }}>
                Reason for rejection:
                <Typography sx={{ ml: 1 }}>
                  {" "}
                  {merchantUserDetails.reject_message}
                </Typography>
              </Typography>
            </>
          )}
          {merchantUserDetails.action_message && (
            <Typography variant="subtitle1" sx={{ mt: 2, display: "flex" }}>
              Action message:
              <Typography sx={{ ml: 1 }}>
                {" "}
                {merchantUserDetails.action_message}
              </Typography>
            </Typography>
          )}
        </Card>
      ) : (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
    </Container>
  );
};

export default AccountDetails;
